// Shared helpers for the Flow Management pages (list + builder)

import { getLocalized } from '../../i18n/getLocalized';

export const MIN_TIMEOUT_MINUTES = 5;
export const MAX_TIMEOUT_MINUTES = 259200; // 180 days

export const EMAIL_PLACEHOLDERS = [
  'recipientName', 'contractNumber', 'contractName', 'counterpartyName',
  'stepName', 'workflowName', 'dueDate', 'companyName',
];

export const emptyEmailTemplate = () => ({ enabled: true, subject: '', body: '' });

// What the assignee may do on the contract while their step is active.
// Review steps default to all-on, approval steps to all-off; information
// steps always carry the all-false set.
export const REVIEW_ACTION_KEYS = [
  'canEditContract', 'canEditDocuments', 'canAddDocuments',
  'canUploadVersions', 'canDownloadDocuments',
];

export const reviewActionsAll = (value) =>
  REVIEW_ACTION_KEYS.reduce((acc, k) => ({ ...acc, [k]: value }), {});

// Per-step abilities toggled in the designer: Görüş Al (request opinions
// without giving up the action), Yönlendir (hand the active action to another
// user) and Harici Gönder (send the contract to someone outside the system).
// All default off; information steps never carry them.
export const STEP_ABILITY_KEYS = ['allowOpinion', 'allowForward', 'allowExternalSend'];

// Dynamic org-chart assignee rules (assigneeType 'organization'). Resolved by
// the backend when the step is reached, not fixed at design time.
export const ORG_RULE_KEYS = [
  'initiator', 'initiator_manager', 'initiator_dept_head',
  'responsible_manager', 'responsible_dept_head',
];

// Information steps only: everyone who has performed an action on the document
// (approvals, uploads, comments, external parties…), resolved when the step
// fires — never fixed at design time.
export const DOC_ACTORS_ORG_RULE = 'document_actors';

// Stable per-step identity, generated client-side so an unsaved step can
// already be referenced (e.g. by the draft requirement) before its first save.
// The server keeps whatever key it receives; reorders and copies preserve it.
export const genStepKey = () =>
  `st-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const emptyStep = (order = 1) => ({
  order,
  stepKey: genStepKey(),
  name: '',
  type: 'approval',
  systemKey: null,
  isActive: true,
  assigneeType: 'user',
  assigneePosition: null,
  assigneeTitle: null,
  assigneeUser: null,
  assigneeGroup: null,
  assigneeOrgRule: null,
  isParallel: false,
  timeoutMinutes: 2880, // 2 days
  escalation: {
    action: 'notify',
    assigneeType: 'user',
    assigneePosition: null,
    assigneeTitle: null,
    assigneeUser: null,
    assigneeGroup: null,
  },
  emails: {
    approval: emptyEmailTemplate(),
    timeout: emptyEmailTemplate(),
    escalation: emptyEmailTemplate(),
  },
  reviewActions: reviewActionsAll(false),
  allowOpinion: false,
  allowForward: false,
  allowExternalSend: false,
  // Multi-assignee completion: 'all' = everyone must decide (legacy default),
  // 'quorum' = quorumCount approvals complete the step.
  approvalPolicy: 'all',
  quorumCount: 1,
});

// Workflow / step names may be multilang objects ({ en, tr }) entered via
// MultiLangInput — resolved with getLocalized. Content seeded before the
// multilang support was stored as combined "TR / EN" strings; those are
// split to the current language's half here. User-authored plain strings
// pass through untouched.
const SEEDED_BILINGUAL = new Set([
  'Standart / Standard',
  'Varsayılan onay iş akışı. / Default approval workflow.',
  'Amir Onayı / Manager Approval',
  'Finans Onayı / Finance Approval',
  'Satın Alma Onayı / Procurement Approval',
  'Hukuk Onayı / Legal Approval',
  'İmzalı Sözleşme / Signed Contract',
]);

const pickHalf = (s, lang) => {
  const [tr, en] = s.split(' / ');
  return String(lang || 'tr').toLowerCase().startsWith('tr') ? tr : en;
};

export const localizeSeeded = (value, lang) => {
  const resolved = getLocalized(value, lang);
  if (typeof resolved !== 'string' || !resolved.includes(' / ')) return resolved;
  // Also handles legacy seeded names embedded in longer server-built strings
  // (e.g. timeline titles like "İmzalı Sözleşme / Signed Contract — Approved").
  let out = resolved;
  for (const s of SEEDED_BILINGUAL) {
    if (out.includes(s)) out = out.split(s).join(pickHalf(s, lang));
  }
  return out;
};

export const emptyWorkflow = () => ({
  name: '',
  description: '',
  // Company scope: [] = All Companies (the standard), ids = specific companies.
  companyIds: [],
  triggerConditions: { contractTypes: [], departments: [], minValue: '', maxValue: '' },
  // Taslak Zorunluluğu: when the draft document becomes mandatory —
  // at workflow start (legacy default) or at a chosen Control step.
  draftRequirement: { mode: 'at_start', stepKey: null },
  // Geri Gönderimde Başlatana Harici Gönder İzni — flow-level and independent
  // of the per-step Harici Gönder ability. Off by default.
  allowExternalSendOnReturn: false,
  steps: [emptyStep(1)],
});

// Extract an id from either a populated object or a raw id
const refId = (v) => (v && typeof v === 'object' ? v._id : v) || null;

// Normalize a workflow coming from the API into the builder form shape.
// The signed-contract step is a system default appended per instance at
// runtime — legacy definitions that still store one hide it here (and the
// backend strips it on save).
export const workflowToForm = (w) => {
  const definedSteps = (w.steps || []).filter((s) => s.systemKey !== 'signed_contract');
  return {
  // name/description/step names stay raw (string | {en,tr}) — the builder
  // edits them through MultiLangInput and displays via localizeSeeded.
  name: w.name || '',
  description: w.description || '',
  // Company scope ids, normalized from possibly-populated refs.
  companyIds: (w.companyIds || []).map((v) => String(refId(v) || v)).filter(Boolean),
  // Standard (SYSTEM) workflows have a fixed step structure: the builder
  // keeps every step setting editable but locks add/copy/delete/reorder and
  // the step type. Not part of the save payload — the server owns the flag.
  isStandard: !!w.isStandard,
  triggerConditions: {
    contractTypes: w.triggerConditions?.contractTypes || [],
    departments: w.triggerConditions?.departments || [],
    minValue: w.triggerConditions?.minValue ?? '',
    maxValue: w.triggerConditions?.maxValue ?? '',
  },
  draftRequirement: {
    mode: w.draftRequirement?.mode === 'at_control_step' ? 'at_control_step' : 'at_start',
    stepKey: w.draftRequirement?.stepKey || null,
  },
  allowExternalSendOnReturn: w.allowExternalSendOnReturn === true,
  steps: (definedSteps.length ? definedSteps : [emptyStep(1)]).map((s, i) => ({
    ...emptyStep(i + 1),
    ...s,
    order: i + 1,
    // Legacy steps come back with stepKey null — give them one so the draft
    // requirement can reference them before the next save persists it.
    stepKey: s.stepKey || genStepKey(),
    name: s.name || '',
    systemKey: s.systemKey || null,
    isActive: s.isActive !== false,
    assigneeType: s.assigneeType || 'user',
    assigneePosition: refId(s.assigneePosition),
    assigneeTitle: refId(s.assigneeTitle),
    assigneeUser: refId(s.assigneeUser),
    assigneeGroup: refId(s.assigneeGroup),
    assigneeOrgRule: s.assigneeOrgRule || null,
    timeoutMinutes: stepTimeoutMinutes(s),
    escalation: {
      action: s.escalation?.action || 'notify',
      assigneeType: s.escalation?.assigneeType || 'user',
      assigneePosition: refId(s.escalation?.assigneePosition),
      assigneeTitle: refId(s.escalation?.assigneeTitle),
      assigneeUser: refId(s.escalation?.assigneeUser),
      assigneeGroup: refId(s.escalation?.assigneeGroup),
    },
    emails: {
      approval: { ...emptyEmailTemplate(), ...(s.emails?.approval || {}) },
      timeout: { ...emptyEmailTemplate(), ...(s.emails?.timeout || {}) },
      escalation: { ...emptyEmailTemplate(), ...(s.emails?.escalation || {}) },
    },
    // Legacy review steps (saved before this feature) behaved as fully
    // permissive — show them that way; legacy approval steps stay all-false.
    reviewActions: s.reviewActions
      ? { ...reviewActionsAll(false), ...s.reviewActions }
      : reviewActionsAll((s.type || 'approval') === 'review'),
    allowOpinion: s.allowOpinion === true,
    allowForward: s.allowForward === true,
    allowExternalSend: s.allowExternalSend === true,
    approvalPolicy: s.approvalPolicy === 'quorum' ? 'quorum' : 'all',
    quorumCount: Math.max(1, Number(s.quorumCount) || 1),
  })),
  };
};

// Effective timeout of a step in minutes (legacy flows only have slaHours)
export const stepTimeoutMinutes = (step) => {
  const m = Number(step?.timeoutMinutes);
  if (Number.isFinite(m) && m > 0) return m;
  const h = Number(step?.slaHours);
  if (Number.isFinite(h) && h > 0) return h * 60;
  return 2880;
};

export const toDHM = (totalMinutes) => {
  const mins = Math.max(0, Math.round(Number(totalMinutes) || 0));
  return {
    days: Math.floor(mins / 1440),
    hours: Math.floor((mins % 1440) / 60),
    minutes: mins % 60,
  };
};

export const fromDHM = ({ days = 0, hours = 0, minutes = 0 }) =>
  (Number(days) || 0) * 1440 + (Number(hours) || 0) * 60 + (Number(minutes) || 0);

// "2d 4h 30m" style label, unit suffixes localized via t()
export const formatDuration = (totalMinutes, t) => {
  const { days, hours, minutes } = toDHM(totalMinutes);
  const parts = [];
  if (days) parts.push(`${days}${t('flows.duration.d')}`);
  if (hours) parts.push(`${hours}${t('flows.duration.h')}`);
  if (minutes) parts.push(`${minutes}${t('flows.duration.m')}`);
  return parts.length ? parts.join(' ') : `0${t('flows.duration.m')}`;
};

// Does the escalation config have a resolvable target?
export const hasEscalationTarget = (esc) => {
  if (!esc) return false;
  switch (esc.assigneeType || 'user') {
    case 'position': return !!esc.assigneePosition;
    case 'title': return !!esc.assigneeTitle;
    case 'group': return !!esc.assigneeGroup;
    default: return !!esc.assigneeUser;
  }
};
